import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Building2 } from 'lucide-react';
import { useCreateSupplier, useUpdateSupplier } from '../../../api/mutations/purchasing.mutations';
import type { Supplier } from '../../../api/queries/purchasing.queries';

interface SupplierModalProps {
  supplier: Supplier | null;
  onClose: () => void;
  onSuccess: () => void;
}

export default function SupplierModal({ supplier, onClose, onSuccess }: SupplierModalProps) {
  const { t } = useTranslation();
  const isEditing = !!supplier;

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    contactPerson: '',
    phone: '',
    alternatePhone: '',
    email: '',
    alternateEmail: '',
    website: '',
    address: '',
    city: '',
    province: '',
    postalCode: '',
    country: 'Thailand',
    taxId: '',
    registrationNumber: '',
    bankName: '',
    accountName: '',
    accountNumber: '',
    branchName: '',
    paymentTerms: '',
    paymentTermsDays: 30,
    creditLimit: 0,
    leadTimeDays: 7,
    minimumOrderValue: 0,
    isPreferred: false,
    notes: '',
  });

  const createSupplier = useCreateSupplier();
  const updateSupplier = useUpdateSupplier(supplier?._id || '');

  useEffect(() => {
    if (supplier) {
      setFormData({
        name: supplier.name || '',
        code: supplier.code || '',
        contactPerson: supplier.contactPerson || '',
        phone: supplier.phone || '',
        alternatePhone: supplier.alternatePhone || '',
        email: supplier.email || '',
        alternateEmail: supplier.alternateEmail || '',
        website: supplier.website || '',
        address: supplier.address || '',
        city: supplier.city || '',
        province: supplier.province || '',
        postalCode: supplier.postalCode || '',
        country: supplier.country || 'Thailand',
        taxId: supplier.taxId || '',
        registrationNumber: supplier.registrationNumber || '',
        bankName: supplier.bankAccount?.bankName || '',
        accountName: supplier.bankAccount?.accountName || '',
        accountNumber: supplier.bankAccount?.accountNumber || '',
        branchName: supplier.bankAccount?.branchName || '',
        paymentTerms: supplier.paymentTerms || '',
        paymentTermsDays: supplier.paymentTermsDays || 30,
        creditLimit: supplier.creditLimit || 0,
        leadTimeDays: supplier.leadTimeDays || 7,
        minimumOrderValue: supplier.minimumOrderValue || 0,
        isPreferred: supplier.isPreferred || false,
        notes: supplier.notes || '',
      });
    }
  }, [supplier]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload = {
      name: formData.name,
      code: formData.code || undefined,
      contactPerson: formData.contactPerson,
      phone: formData.phone,
      alternatePhone: formData.alternatePhone || undefined,
      email: formData.email,
      alternateEmail: formData.alternateEmail || undefined,
      website: formData.website || undefined,
      address: formData.address,
      city: formData.city || undefined,
      province: formData.province || undefined,
      postalCode: formData.postalCode || undefined,
      country: formData.country,
      taxId: formData.taxId || undefined,
      registrationNumber: formData.registrationNumber || undefined,
      bankAccount: {
        bankName: formData.bankName || undefined,
        accountName: formData.accountName || undefined,
        accountNumber: formData.accountNumber || undefined,
        branchName: formData.branchName || undefined,
      },
      paymentTerms: formData.paymentTerms || undefined,
      paymentTermsDays: formData.paymentTermsDays,
      creditLimit: formData.creditLimit || undefined,
      leadTimeDays: formData.leadTimeDays,
      minimumOrderValue: formData.minimumOrderValue || undefined,
      isPreferred: formData.isPreferred,
      notes: formData.notes || undefined,
    };

    try {
      if (isEditing) {
        await updateSupplier.mutateAsync(payload);
      } else {
        await createSupplier.mutateAsync(payload);
      }
      onSuccess();
    } catch (error) {
      console.error('Failed to save supplier:', error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      onKeyDown={handleKeyDown}
    >
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-lg bg-card flex flex-col">
        {/* Header */}
        <div className="border-b p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            <h2 className="text-xl font-bold">
              {isEditing ? t('purchasing.editSupplier') : t('purchasing.addSupplier')}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-secondary rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Basic Info */}
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3">
                {t('purchasing.basicInfo')}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-sm font-medium mb-1">
                    {t('purchasing.supplierName')} *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full rounded-lg border bg-background px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t('purchasing.supplierCode')}
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    className="w-full rounded-lg border bg-background px-3 py-2"
                    placeholder="e.g., SUP-001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t('purchasing.contactPerson')} *
                  </label>
                  <input
                    type="text"
                    value={formData.contactPerson}
                    onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                    className="w-full rounded-lg border bg-background px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t('purchasing.phone')} *
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full rounded-lg border bg-background px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t('purchasing.email')} *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full rounded-lg border bg-background px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t('purchasing.website')}
                  </label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="w-full rounded-lg border bg-background px-3 py-2"
                    placeholder="https://"
                  />
                </div>
              </div>
            </div>

            {/* Address */}
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3">
                {t('purchasing.address')}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">
                    {t('purchasing.streetAddress')} *
                  </label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full rounded-lg border bg-background px-3 py-2"
                    rows={2}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{t('purchasing.city')}</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full rounded-lg border bg-background px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t('purchasing.province')}
                  </label>
                  <input
                    type="text"
                    value={formData.province}
                    onChange={(e) => setFormData({ ...formData, province: e.target.value })}
                    className="w-full rounded-lg border bg-background px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t('purchasing.postalCode')}
                  </label>
                  <input
                    type="text"
                    value={formData.postalCode}
                    onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                    className="w-full rounded-lg border bg-background px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t('purchasing.country')}
                  </label>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    className="w-full rounded-lg border bg-background px-3 py-2"
                  />
                </div>
              </div>
            </div>

            {/* Business Info */}
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3">
                {t('purchasing.businessInfo')}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">{t('purchasing.taxId')}</label>
                  <input
                    type="text"
                    value={formData.taxId}
                    onChange={(e) => setFormData({ ...formData, taxId: e.target.value })}
                    className="w-full rounded-lg border bg-background px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t('purchasing.paymentTermsDays')}
                  </label>
                  <input
                    type="number"
                    value={formData.paymentTermsDays}
                    onChange={(e) =>
                      setFormData({ ...formData, paymentTermsDays: parseInt(e.target.value) || 0 })
                    }
                    className="w-full rounded-lg border bg-background px-3 py-2"
                    min={0}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t('purchasing.leadTimeDays')}
                  </label>
                  <input
                    type="number"
                    value={formData.leadTimeDays}
                    onChange={(e) =>
                      setFormData({ ...formData, leadTimeDays: parseInt(e.target.value) || 0 })
                    }
                    className="w-full rounded-lg border bg-background px-3 py-2"
                    min={0}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t('purchasing.minimumOrderValue')}
                  </label>
                  <input
                    type="number"
                    value={formData.minimumOrderValue}
                    onChange={(e) =>
                      setFormData({ ...formData, minimumOrderValue: parseInt(e.target.value) || 0 })
                    }
                    className="w-full rounded-lg border bg-background px-3 py-2"
                    min={0}
                  />
                </div>
              </div>
            </div>

            {/* Bank Account */}
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3">
                {t('purchasing.bankAccount')}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t('purchasing.bankName')}
                  </label>
                  <input
                    type="text"
                    value={formData.bankName}
                    onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                    className="w-full rounded-lg border bg-background px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t('purchasing.accountName')}
                  </label>
                  <input
                    type="text"
                    value={formData.accountName}
                    onChange={(e) => setFormData({ ...formData, accountName: e.target.value })}
                    className="w-full rounded-lg border bg-background px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t('purchasing.accountNumber')}
                  </label>
                  <input
                    type="text"
                    value={formData.accountNumber}
                    onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                    className="w-full rounded-lg border bg-background px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t('purchasing.bankBranch')}
                  </label>
                  <input
                    type="text"
                    value={formData.branchName}
                    onChange={(e) => setFormData({ ...formData, branchName: e.target.value })}
                    className="w-full rounded-lg border bg-background px-3 py-2"
                  />
                </div>
              </div>
            </div>

            {/* Preferences */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isPreferred}
                  onChange={(e) => setFormData({ ...formData, isPreferred: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm font-medium">{t('purchasing.markAsPreferred')}</span>
              </label>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium mb-1">{t('common.notes')}</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full rounded-lg border bg-background px-3 py-2"
                rows={3}
              />
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="border-t p-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border hover:bg-secondary transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={createSupplier.isPending || updateSupplier.isPending}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {createSupplier.isPending || updateSupplier.isPending
              ? t('common.saving')
              : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
}
